import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, Gift, Award, ChevronDown, ChevronUp, Check, Info } from 'lucide-react';
import { useSelector } from 'react-redux';
// import components
import Header from '../components/Header';
import Footer from '../components/Footer';

const Plan = () => {

    // hooks
    const { isAuth } = useSelector((state) => state.auth);
    const [expandedLevel, setExpandedLevel] = useState(null);

    const planData = [
        { level: 1, membersLeft: 1, right: 1, income: 500, development: '', cashAward: '', remarks: '' },
        { level: 2, membersLeft: 2, right: 2, income: 1000, development: '', cashAward: '', remarks: '' },
        { level: 3, membersLeft: 4, right: 4, income: 2000, development: '', cashAward: '', remarks: '' },
        { level: 4, membersLeft: 8, right: 8, income: 4000, development: '', cashAward: '', remarks: '' },
        { level: 5, membersLeft: 16, right: 16, income: 8000, development: '', cashAward: '', remarks: '₹2000 refund' },
        { level: 6, membersLeft: 32, right: 32, income: 16000, development: '', cashAward: '', remarks: '' },
        { level: 7, membersLeft: 64, right: 64, income: 32000, development: '', cashAward: '5000', remarks: '' },
        { level: 8, membersLeft: 128, right: 128, income: 64000, development: '', cashAward: '10000', remarks: '' },
        { level: 9, membersLeft: 256, right: 256, income: 128000, development: '₹2000 for 6 months', cashAward: '15000', remarks: '' },
        { level: 10, membersLeft: 512, right: 512, income: 256000, development: '₹5000 for 6 months', cashAward: '25000', remarks: '' },
        { level: 11, membersLeft: 1024, right: 1024, income: 512000, development: '₹7000 for 6 months', cashAward: '50000', remarks: '' },
        { level: 12, membersLeft: 2048, right: 2048, income: 1024000, development: '₹10000 for 1 year', cashAward: '100000', remarks: '' },
        { level: 13, membersLeft: 4096, right: 4096, income: 2048000, development: '₹15000 for 1 year', cashAward: '200000', remarks: 'Director Status' }
    ];

    const toggleLevel = (level) => {
        setExpandedLevel(expandedLevel === level ? null : level);
    };

    const features = [
        {
            icon: Users,
            title: 'Binary Tree System',
            description: 'Build your network with 1-2 or 2-1 placement strategy'
        },
        {
            icon: TrendingUp,
            title: 'Progressive Income',
            description: 'Income doubles at each level as your network grows'
        },
        {
            icon: Gift,
            title: 'Cash Awards',
            description: 'Earn up to ₹2,00,000 in cash awards'
        },
        {
            icon: Award,
            title: 'Director Bonus',
            description: '2% of company income shared among Level 13 achievers'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                </div>
                <Header />
                <div className="relative z-10 px-6 py-20">
                    <div className="max-w-7xl mx-auto text-center">
                        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                            Gateway Promoters <span className="text-cyan-400">Binary Plan</span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                            Joining Fee: ₹2000 | Binary Plan: 1-2 or 2-1 | Products: Welcome Kit
                        </p>
                        <div className="inline-block bg-gradient-to-r from-cyan-500/20 to-blue-600/20 rounded-full px-6 py-3 border border-cyan-500/30">
                            <p className="text-cyan-400 font-semibold">Total Earnings Potential: ₹40,95,500 | Cash Awards: ₹4,05,000 | Business Development: ₹3,84,000</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-12">
                <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-6">
                    {features.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <div
                                key={index}
                                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/20 hover:border-cyan-500/50 transition-all text-center"
                            >
                                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                                    <Icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                                <p className="text-gray-400 text-sm">{feature.description}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="px-6 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Level-wise Income <span className="text-cyan-400">Structure</span>
                        </h2>
                        <p className="text-xl text-gray-400">Complete breakdown of earnings at each level</p>
                    </div>

                    <div className="hidden md:block bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-cyan-500/20 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-cyan-500/20 to-blue-600/20">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Level</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Members Left</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Right</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Binary Income</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Development Fund</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Cash Award</th>
                                        <th className="px-6 py-4 text-left text-cyan-400 font-bold">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {planData.map((row, index) => (
                                        <tr key={index} className="border-b border-cyan-500/10 hover:bg-cyan-500/5 transition-colors">
                                            <td className="px-6 py-4 text-white font-semibold">{row.level}</td>
                                            <td className="px-6 py-4 text-gray-300">{row.membersLeft}</td>
                                            <td className="px-6 py-4 text-gray-300">{row.right}</td>
                                            <td className="px-6 py-4 text-cyan-400 font-semibold">₹{row.income.toLocaleString()}</td>
                                            <td className="px-6 py-4 text-gray-300 text-sm">{row.development || '-'}</td>
                                            <td className="px-6 py-4 text-green-400 font-semibold">{row.cashAward ? `₹${row.cashAward}` : '-'}</td>
                                            <td className="px-6 py-4 text-yellow-400 text-sm">{row.remarks || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="md:hidden space-y-4">
                        {planData.map((row, index) => (
                            <div key={index} className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-cyan-500/20 overflow-hidden">
                                <button
                                    onClick={() => toggleLevel(row.level)}
                                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-cyan-500/5 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <span className="text-white font-bold text-lg">Level {row.level}</span>
                                        <span className="text-cyan-400 font-semibold">₹{row.income.toLocaleString()}</span>
                                    </div>
                                    {expandedLevel === row.level ? (
                                        <ChevronUp className="w-5 h-5 text-cyan-400" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-cyan-400" />
                                    )}
                                </button>

                                {expandedLevel === row.level && (
                                    <div className="px-6 py-4 space-y-3 border-t border-cyan-500/20">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Members:</span>
                                            <span className="text-white">{row.membersLeft} Left | {row.right} Right</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Binary Income:</span>
                                            <span className="text-cyan-400 font-semibold">₹{row.income.toLocaleString()}</span>
                                        </div>
                                        {row.development && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Development Fund:</span>
                                                <span className="text-white">{row.development}</span>
                                            </div>
                                        )}
                                        {row.cashAward && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Cash Award:</span>
                                                <span className="text-green-400 font-semibold">₹{row.cashAward}</span>
                                            </div>
                                        )}
                                        {row.remarks && (
                                            <div className="flex justify-between">
                                                <span className="text-gray-400">Remarks:</span>
                                                <span className="text-yellow-400">{row.remarks}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-6 py-20 bg-slate-800/50">
                <div className="max-w-5xl mx-auto">
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30">
                        <div className="flex items-start gap-4 mb-6">
                            <Info className="w-8 h-8 text-cyan-400 flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="text-2xl font-bold text-white mb-4">Important Terms & Conditions</h3>
                                <div className="space-y-4 text-gray-300">
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Daily Payout:</span> Earn daily on a maximum of 10 pairs only (₹3,000 max daily earning)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Weekly Payout:</span> Receive 5 PINs after purchase of 50 PINs every Wednesday</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Maximum Daily Income:</span> One person may have a maximum of 7 IDs, earning up to ₹21,000 daily</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Purchase Requirement:</span> Purchase 100 PINs to receive 12 PINs free (Total: 112 PINs)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Administrative Charge:</span> 5% charge and TDS 5% (Total: 10% deduction)</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                                        <p><span className="text-white font-semibold">Director Bonus:</span> Level 13 achievers receive 2% of company's total income distributed equally</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/20">
                            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center mb-4">
                                <Gift className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Joining Fee Refund</h3>
                            <p className="text-gray-300 mb-4">Get your ₹2000 joining fee back at Level 5</p>
                            <p className="text-cyan-400 font-semibold">Total Income at Level 5: ₹8000 + ₹2000 Refund</p>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/20">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg flex items-center justify-center mb-4">
                                <Award className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Total Cash Awards</h3>
                            <p className="text-gray-300 mb-4">Earn substantial cash awards from Level 7 onwards</p>
                            <p className="text-cyan-400 font-semibold">Maximum Cash Awards: ₹4,05,000</p>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/20">
                            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                                <TrendingUp className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Development Fund</h3>
                            <p className="text-gray-300 mb-4">Monthly business development support from Level 9</p>
                            <p className="text-cyan-400 font-semibold">Total Development Fund: ₹3,84,000</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-20 bg-gradient-to-r from-cyan-500 to-blue-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Start Building Your Network?
                    </h2>
                    <p className="text-xl text-white/90 mb-8">
                        Join Gateway Promoters today and unlock your path to financial freedom
                    </p>
                    {
                        isAuth ? (
                            <Link to="/dashboard" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                Get Started
                            </Link>

                        ) : (
                            <Link to="/register" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                Register Now - ₹2000 Only
                            </Link>
                        )
                    }
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Plan;